import { PostgrestResponse } from "@supabase/supabase-js";
import { ISaldosFuncionarios } from "../../../types/tables/ISaldosFuncionarios";
import { BaseMultiTenantService } from "../base/BaseMultiTenantService";

/**
 * Serviço para gerenciamento de saldos de funcionários com isolamento multi-tenant
 * Gerencia créditos disponíveis para funcionários gastarem na cantina
 */
export class SaldosFuncionariosService extends BaseMultiTenantService<ISaldosFuncionarios> {
  constructor() {
    super("saldos_funcionarios", [], "fk_id_organizacao");
  }

  /**
   * Busca saldo de um funcionário específico
   */
  async getSaldoByFuncionario(
    organizationId: number,
    funcionarioId: number,
  ): Promise<PostgrestResponse<ISaldosFuncionarios>> {
    return await this.client
      .from(this.tableName)
      .select("*")
      .eq(this.organizationIdField, organizationId)
      .eq("fk_id_funcionario", funcionarioId)
      .single();
  }

  /**
   * Adiciona crédito ao saldo do funcionário
   */
  async adicionarCredito(
    organizationId: number,
    funcionarioId: number,
    valor: number,
    descricao: string,
    userId?: number,
  ): Promise<PostgrestResponse<ISaldosFuncionarios[]>> {
    // Buscar saldo atual
    const { data: saldoAtualRaw } = await this.getSaldoByFuncionario(
      organizationId,
      funcionarioId,
    );
    const saldoAtual = saldoAtualRaw as any; // Conversão temporária

    let novoSaldo: number;
    let resultado: any;

    if (saldoAtual) {
      // Atualizar saldo existente
      novoSaldo = saldoAtual.saldo_atual + valor;
      resultado = await this.update(
        saldoAtual.id,
        {
          saldo_atual: novoSaldo,
          updated_at: new Date().toISOString(),
        },
        organizationId,
        userId,
      );
    } else {
      // Criar novo registro de saldo
      novoSaldo = valor;
      resultado = await this.create(
        {
          fk_id_funcionario: funcionarioId,
          saldo_atual: novoSaldo,
          saldo_bloqueado: 0,
          limite_diario: 0,
          limite_semanal: 0,
        },
        organizationId,
        userId,
      );
    }

    // Registrar movimentação
    await this.client.from("movimentacoes_saldo").insert({
      fk_id_pessoa: funcionarioId,
      fk_id_organizacao: organizationId,
      tipo_movimentacao: "credito",
      tipo_origem: "deposito",
      valor: valor,
      saldo_anterior: saldoAtual?.saldo_atual || 0,
      saldo_posterior: novoSaldo,
      descricao: descricao,
      data_movimentacao: new Date().toISOString(),
      created_by: userId,
    });

    return resultado;
  }

  /**
   * Debita valor do saldo do funcionário (para compras)
   */
  async debitarSaldo(
    organizationId: number,
    funcionarioId: number,
    valor: number,
    vendaId: number,
    descricao: string,
    userId?: number,
  ): Promise<PostgrestResponse<ISaldosFuncionarios[]>> {
    const { data: saldoAtualRaw } = await this.getSaldoByFuncionario(
      organizationId,
      funcionarioId,
    );
    const saldoAtual = saldoAtualRaw as any; // Conversão temporária

    if (!saldoAtual) {
      throw new Error("Funcionário não possui saldo cadastrado");
    }

    const saldoDisponivel = saldoAtual.saldo_atual - saldoAtual.saldo_bloqueado;
    if (saldoDisponivel < valor) {
      throw new Error("Saldo insuficiente");
    }

    const novoSaldo = saldoAtual.saldo_atual - valor;

    // Atualizar saldo
    const resultado = await this.update(
      saldoAtual.id,
      {
        saldo_atual: novoSaldo,
        updated_at: new Date().toISOString(),
      },
      organizationId,
      userId,
    );

    // Registrar movimentação
    await this.client.from("movimentacoes_saldo").insert({
      fk_id_pessoa: funcionarioId,
      fk_id_organizacao: organizationId,
      tipo_movimentacao: "debito",
      tipo_origem: "compra",
      origem_id: vendaId,
      valor: valor,
      saldo_anterior: saldoAtual.saldo_atual,
      saldo_posterior: novoSaldo,
      descricao: descricao,
      data_movimentacao: new Date().toISOString(),
      created_by: userId,
    });

    return resultado;
  }

  /**
   * Define limites de gasto para o funcionário
   */
  async definirLimites(
    organizationId: number,
    funcionarioId: number,
    limiteDiario: number,
    limiteSemanal: number,
    userId?: number,
  ): Promise<PostgrestResponse<ISaldosFuncionarios[]>> {
    const { data: saldoAtualRaw } = await this.getSaldoByFuncionario(
      organizationId,
      funcionarioId,
    );
    const saldoAtual = saldoAtualRaw as any; // Conversão temporária

    if (!saldoAtual) {
      throw new Error("Funcionário não possui saldo cadastrado");
    }

    return await this.update(
      saldoAtual.id,
      {
        limite_diario: limiteDiario,
        limite_semanal: limiteSemanal,
        updated_at: new Date().toISOString(),
      },
      organizationId,
      userId,
    );
  }

  /**
   * Verifica se o funcionário pode realizar uma compra
   */
  async verificarLimiteCompra(
    organizationId: number,
    funcionarioId: number,
    valorCompra: number,
  ): Promise<{
    podeComprar: boolean;
    motivo?: string;
    saldoDisponivel: number;
    limiteDiarioRestante: number;
    limiteSemanalRestante: number;
  }> {
    const { data: saldoAtualRaw } = await this.getSaldoByFuncionario(
      organizationId,
      funcionarioId,
    );
    const saldoAtual = saldoAtualRaw as any;

    if (!saldoAtual) {
      return {
        podeComprar: false,
        motivo: "Funcionário não possui saldo cadastrado",
        saldoDisponivel: 0,
        limiteDiarioRestante: 0,
        limiteSemanalRestante: 0,
      };
    }

    const saldoDisponivel = saldoAtual.saldo_atual - saldoAtual.saldo_bloqueado;

    if (saldoDisponivel < valorCompra) {
      return {
        podeComprar: false,
        motivo: "Saldo insuficiente",
        saldoDisponivel,
        limiteDiarioRestante: saldoAtual.limite_diario || 0,
        limiteSemanalRestante: saldoAtual.limite_semanal || 0,
      };
    }

    return {
      podeComprar: true,
      saldoDisponivel,
      limiteDiarioRestante: saldoAtual.limite_diario || 0,
      limiteSemanalRestante: saldoAtual.limite_semanal || 0,
    };
  }

  /**
   * Bloqueia valor no saldo (para transações pendentes)
   */
  async bloquearSaldo(
    organizationId: number,
    funcionarioId: number,
    valor: number,
    userId?: number,
  ): Promise<PostgrestResponse<ISaldosFuncionarios[]>> {
    const { data: saldoAtualRaw } = await this.getSaldoByFuncionario(
      organizationId,
      funcionarioId,
    );
    const saldoAtual = saldoAtualRaw as any; // Conversão temporária

    if (!saldoAtual) {
      throw new Error("Funcionário não possui saldo cadastrado");
    }

    const saldoDisponivel = saldoAtual.saldo_atual - saldoAtual.saldo_bloqueado;
    if (saldoDisponivel < valor) {
      throw new Error("Saldo insuficiente para bloqueio");
    }

    return await this.update(
      saldoAtual.id,
      {
        saldo_bloqueado: saldoAtual.saldo_bloqueado + valor,
        updated_at: new Date().toISOString(),
      },
      organizationId,
      userId,
    );
  }

  /**
   * Desbloqueia valor no saldo
   */
  async desbloquearSaldo(
    organizationId: number,
    funcionarioId: number,
    valor: number,
    userId?: number,
  ): Promise<PostgrestResponse<ISaldosFuncionarios[]>> {
    const { data: saldoAtualRaw } = await this.getSaldoByFuncionario(
      organizationId,
      funcionarioId,
    );
    const saldoAtual = saldoAtualRaw as any; // Conversão temporária

    if (!saldoAtual) {
      throw new Error("Funcionário não possui saldo cadastrado");
    }

    return await this.update(
      saldoAtual.id,
      {
        saldo_bloqueado: Math.max(0, saldoAtual.saldo_bloqueado - valor),
        updated_at: new Date().toISOString(),
      },
      organizationId,
      userId,
    );
  }

  /**
   * Busca todos os saldos com informações dos funcionários
   */
  async getAllWithFuncionarios(
    organizationId: number,
  ): Promise<PostgrestResponse<ISaldosFuncionarios[]>> {
    return await this.client
      .from(this.tableName)
      .select(
        `
                    *,
                    funcionario:fk_id_funcionario(id, nome, email)
               `,
      )
      .eq(this.organizationIdField, organizationId);
  }

  /**
   * Relatório de consumo do funcionário
   */
  async getRelatorioConsumo(
    organizationId: number,
    funcionarioId: number,
  ): Promise<PostgrestResponse<ISaldosFuncionarios[]>> {
    return await this.client
      .from("movimentacoes_saldo")
      .select("*")
      .eq("fk_id_organizacao", organizationId)
      .eq("fk_id_pessoa", funcionarioId)
      .order("data_movimentacao", { ascending: false });
  }
}

// Instância singleton
export const saldosFuncionariosService = new SaldosFuncionariosService();
